import { useState, useRef, type FormEvent } from 'react'
import { Layout } from '../components/layout/Layout'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import { assets } from '../config/assets'
import { logGaEvent } from '../lib/analytics'
import {
  SmsConsentCheckbox,
  SMS_CONSENT_TEXT,
  SMS_CONSENT_VERSION,
} from '../components/forms/SmsConsentCheckbox'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

interface FormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  message: string
}

const EMPTY_FORM: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
}

export function ContactPage() {
  const [form, setForm] = useState<FormData>(EMPTY_FORM)
  const [honeypotValue, setHoneypotValue] = useState('')
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // A2P 10DLC: deliberately outside FormData. handleRetry restores a saved
  // FormData snapshot, and consent must never be re-asserted by a state
  // restore. It stays exactly where the person left the visible checkbox.
  const [smsConsent, setSmsConsent] = useState(false)
  const savedForm = useRef<FormData | null>(null)

  function set<K extends keyof FormData>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const canSubmit =
    status !== 'loading' &&
    form.firstName.trim() !== '' &&
    form.email.trim() !== '' &&
    form.message.trim() !== ''

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!canSubmit) return

    savedForm.current = { ...form }
    setStatus('loading')
    setErrorMessage(null)

    try {
      const res = await fetch('/api/submitForm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'contact',
          payload: {
            first_name: form.firstName,
            last_name: form.lastName,
            email: form.email,
            phone: form.phone,
            message: form.message,
            // Consent is only ever claimed when they gave us a number AND
            // ticked the box themselves. Never inferred from either alone.
            sms_consent: smsConsent && form.phone.trim().length > 0,
            sms_consent_text:
              smsConsent && form.phone.trim().length > 0 ? SMS_CONSENT_TEXT : '',
            sms_consent_version:
              smsConsent && form.phone.trim().length > 0 ? SMS_CONSENT_VERSION : '',
          },
          honeypot: honeypotValue,
        }),
      })

      if (!res.ok) {
        try {
          const data = (await res.json()) as { message?: string }
          if (typeof data.message === 'string' && data.message.length > 0) {
            setErrorMessage(data.message)
          }
        } catch {
          // Body unparseable — fall through to generic error.
        }
        setStatus('error')
        return
      }
      setStatus('success')
      // Contact form submitted successfully (a lead). This form shows inline
      // success rather than redirecting, so the event fires here. GA key event.
      logGaEvent('generate_lead', { form: 'contact' })
    } catch {
      setStatus('error')
    }
  }

  function handleRetry() {
    if (savedForm.current) {
      setForm(savedForm.current)
    }
    setErrorMessage(null)
    setStatus('idle')
  }

  return (
    <Layout>
      <ManagedSeo path="/contact" />
      {/* ─── HEADER ──────────────────────────────────────────────────────── */}
      <section className="bg-navy py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-3">
            Get in Touch
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-semibold leading-tight mb-4">
            Let's start a conversation.
          </h1>
          <p className="font-body text-base text-white/60 max-w-xl mx-auto leading-relaxed">
            Have a question about coaching, masterclasses, or working with Scott?
            Fill out the form below and the team will get back to you.
          </p>
        </div>
      </section>

      {/* ─── FORM ────────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Photo — above form on mobile, right side on desktop */}
            <div className="order-1 lg:order-2">
              <img
                src={assets.scott.coachingStirrup}
                alt="Scott Purdum adjusting a rider's stirrup during a private coaching session"
                width={600}
                height={500}
                loading="lazy"
                className="w-full max-h-[280px] lg:max-h-none object-cover object-center rounded-lg"
              />
            </div>

            {/* Form column */}
            <div className="order-2 lg:order-1 max-w-xl">
              <p className="font-body text-sm text-navy/70 leading-relaxed mb-8">
                Scott and his team personally review every message. You will hear
                back within 48 hours.
              </p>

          {status === 'success' ? (
            <SuccessState />
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot — bots fill this, humans never see it. */}
              <input
                type="text"
                name="website"
                value={honeypotValue}
                onChange={(e) => setHoneypotValue(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              />
              <div className="space-y-5 mb-8">
                {/* First + Last name row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    id="contact-first-name"
                    label="First Name"
                    required
                  >
                    <input
                      id="contact-first-name"
                      type="text"
                      value={form.firstName}
                      onChange={(e) => set('firstName', e.target.value)}
                      required
                      autoComplete="given-name"
                      placeholder="Your first name"
                      className={inputClasses}
                      style={inputStyle}
                    />
                  </Field>
                  <Field
                    id="contact-last-name"
                    label="Last Name"
                  >
                    <input
                      id="contact-last-name"
                      type="text"
                      value={form.lastName}
                      onChange={(e) => set('lastName', e.target.value)}
                      autoComplete="family-name"
                      placeholder="Your last name"
                      className={inputClasses}
                      style={inputStyle}
                    />
                  </Field>
                </div>

                {/* Email */}
                <Field id="contact-email" label="Email Address" required>
                  <input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="your@email.com"
                    className={inputClasses}
                    style={inputStyle}
                  />
                </Field>

                {/* Phone — optional, and optional on purpose. */}
                <Field id="contact-phone" label="Mobile Phone" optional>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    autoComplete="tel"
                    maxLength={50}
                    placeholder="(540) 555-0123"
                    className={inputClasses}
                    style={inputStyle}
                  />
                  <p className="font-body text-xs text-navy/50 mt-1 leading-relaxed">
                    Add a mobile number only if a text reply is easier than
                    email. You will hear back either way.
                  </p>
                </Field>

                {/* Message */}
                <Field id="contact-message" label="Message" required>
                  <textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(e) => set('message', e.target.value)}
                    required
                    rows={5}
                    placeholder="How can we help?"
                    className={`${inputClasses} resize-y`}
                    style={inputStyle}
                  />
                </Field>

                {/* A2P 10DLC: its own box, unchecked, never required. */}
                <div className="pt-2 border-t border-navy/10">
                  <SmsConsentCheckbox
                    id="contact-sms-consent"
                    checked={smsConsent}
                    onChange={setSmsConsent}
                  />
                </div>
              </div>

              {status === 'error' && (
                <p className="font-body text-sm text-red-600 mb-4">
                  {errorMessage ?? 'Something went wrong.'}{' '}
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="underline font-semibold hover:opacity-80 transition-opacity"
                  >
                    Try again
                  </button>
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  backgroundColor: canSubmit ? '#C8A84B' : '#a08940',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 32px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase' as const,
                }}
              >
                {status === 'loading' ? 'Sending...' : 'Send Message'}
              </button>

              <p className="font-body text-xs text-navy/40 text-center mt-4">
                Prefer email? Reach us at info@advantagehorsemanship.com
              </p>
            </form>
          )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  )
}

// ─── SUCCESS STATE ─────────────────────────────────────────────────────────

function SuccessState() {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-8 h-8 text-gold"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h2 className="font-heading text-2xl sm:text-3xl text-navy font-semibold mb-3">
        Message sent.
      </h2>
      <p className="font-body text-sm text-navy/60 leading-relaxed max-w-sm mx-auto">
        Thank you for reaching out. The team will get back to you shortly.
      </p>
    </div>
  )
}

// ─── FIELD WRAPPER ─────────────────────────────────────────────────────────

function Field({
  id,
  label,
  required,
  optional,
  children,
}: {
  id: string
  label: string
  required?: boolean
  optional?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <label
        className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
        htmlFor={id}
      >
        {label}
        {required && ' *'}
        {optional && (
          <span className="text-navy/40 normal-case font-normal tracking-normal">
            {' '}
            (optional)
          </span>
        )}
      </label>
      {children}
    </div>
  )
}

// ─── SHARED STYLES ─────────────────────────────────────────────────────────

const inputClasses =
  'w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy bg-white'

const inputStyle = { fontSize: '16px' } as const
